import React, { useState, useEffect, useCallback } from 'react';
import { api, authHeaders, fmtDM, PROJECTS, PROJECT_COLOR, CHANNELS, CHANNEL_CLASS, salesmenFor, isCateringProject } from './marketingShared';
import { useAuth } from '../contexts/AuthContext';
import SearchSelect from './SearchSelect';
import { leadProjectsForUser } from './marketingAccess';
import './Marketing.css';

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Catering projects use "Ads"; the W-projects are event spaces
// (use Type of Event / Date of Event / Pax / Company / Email).
const isEventSpace = (project) => !isCateringProject(project);

const defaultSalesman = (project, salesmen = salesmenFor(project)) => {
  const preferred = salesmenFor(project)[0];
  return salesmen.includes(preferred) ? preferred : (salesmen.find((name) => name !== 'Junk') || '');
};

const emptyForm = (project, salesmen) => ({
  enquiry_date: todayISO(),
  name: '', contact: '', channel: 'FB', salesman: defaultSalesman(project, salesmen),
  ads: '',                                  // RMC only
  event_type: '', event_date: '', pax: '', company: '', email: '', // event space only
  remarks: '',
});

function MarketingLeads({ month }) {
  const { user } = useAuth();
  const visibleProjects = leadProjectsForUser(user, PROJECTS);
  const initialProject = visibleProjects[0] || 'RMC';
  const [project, setProject] = useState(initialProject);
  const [rows, setRows] = useState([]);
  const [activeSalesmen, setActiveSalesmen] = useState(null);
  const [salespeopleError, setSalespeopleError] = useState('');
  const [counts, setCounts] = useState({});
  const [form, setForm] = useState(emptyForm(initialProject));
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [adsOptions, setAdsOptions] = useState([]);
  const [addingAd, setAddingAd] = useState(false);
  const [newAd, setNewAd] = useState('');
  const [addingAdEdit, setAddingAdEdit] = useState(false);
  const [newAdEdit, setNewAdEdit] = useState('');
  const [filters, setFilters] = useState({ q: '', date: '', channel: '', salesman: '', ads: '' });
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(0);

  const evt = isEventSpace(project);
  const clearFilters = () => setFilters({ q: '', date: '', channel: '', salesman: '', ads: '' });
  const activeFilters = filters.q || filters.date || filters.channel || filters.salesman || filters.ads;

  const loadAds = useCallback(async () => {
    if (isEventSpace(project)) { setAdsOptions([]); return; }
    try {
      const res = await api.get(`/ads?project=${encodeURIComponent(project)}`, { headers: authHeaders() });
      setAdsOptions(res.data.rows);
    } catch { setAdsOptions([]); }
  }, [project]);

  useEffect(() => {
    let mounted = true;
    api.get('/salespeople', { headers: authHeaders() })
      .then((res) => {
        if (!mounted) return;
        const names = [...new Set((res.data.salespeople || []).filter(Boolean))];
        const choices = [...names, 'Junk'];
        setActiveSalesmen(names);
        setForm((current) => choices.includes(current.salesman)
          ? current
          : { ...current, salesman: names[0] || '' });
      })
      .catch(() => { if (mounted) setSalespeopleError('Could not load the current salesperson list. Showing the usual project choices.'); });
    return () => { mounted = false; };
  }, []);

  const loadCounts = useCallback(async () => {
    try {
      const res = await api.get(`/report?month=${month}`, { headers: authHeaders() });
      setCounts(Object.fromEntries(res.data.byProject.map((r) => [r.project, r.count])));
    } catch { /* non-fatal */ }
  }, [month]);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await api.get(`/leads?month=${month}&project=${encodeURIComponent(project)}`, { headers: authHeaders() });
      setRows(res.data.rows);
    } catch (e) { setError('Failed to load leads'); }
  }, [month, project]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => { loadAds(); }, [loadAds]);
  useEffect(() => { setPage(0); }, [project, month, filters, pageSize]);

  const currentSalesmen = activeSalesmen === null ? salesmenFor(project) : [...activeSalesmen, 'Junk'];
  const switchProject = (p) => { setProject(p); setEditing(null); setForm(emptyForm(p, activeSalesmen === null ? salesmenFor(p) : currentSalesmen)); setAddingAd(false); setNewAd(''); clearFilters(); };

  const lc = (s) => (s || '').toLowerCase();
  const filteredRows = rows.filter((r) => {
    if (filters.q) { const q = lc(filters.q); if (!lc(r.name).includes(q) && !lc(r.contact).includes(q)) return false; }
    if (filters.date && r.enquiry_date !== filters.date) return false;
    if (filters.channel && r.channel !== filters.channel) return false;
    if (filters.salesman && r.salesman !== filters.salesman) return false;
    if (!evt && filters.ads && r.ads !== filters.ads) return false;
    return true;
  });

  const addAd = async () => {
    const name = newAd.trim();
    if (!name) { setAddingAd(false); return; }
    try {
      await api.post('/ads', { project, name }, { headers: authHeaders() });
      await loadAds();
      setForm((f) => ({ ...f, ads: name }));
      setNewAd(''); setAddingAd(false);
    } catch (e) { setError(e.response?.data?.error || 'Could not add ad'); }
  };

  const addAdEdit = async () => {
    const name = newAdEdit.trim();
    if (!name) { setAddingAdEdit(false); return; }
    try {
      await api.post('/ads', { project, name }, { headers: authHeaders() });
      await loadAds();
      setEditing((ed) => ({ ...ed, ads: name }));
      setNewAdEdit(''); setAddingAdEdit(false);
    } catch (e) { setError(e.response?.data?.error || 'Could not add ad'); }
  };

  const startEdit = (r) => { setEditing(r); setAddingAdEdit(false); setNewAdEdit(''); };

  const add = async () => {
    if (!form.enquiry_date) { setError('Date is required'); return; }
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.contact.trim()) { setError('Contact number/name is required'); return; }
    setSaving(true); setError('');
    try {
      await api.post('/leads', { ...form, project }, { headers: authHeaders() });
      setForm(emptyForm(project, currentSalesmen));
      await load(); await loadCounts();
    } catch (e) { setError(e.response?.data?.error || 'Could not add lead'); }
    finally { setSaving(false); }
  };

  const saveEdit = async () => {
    if (!editing.name || !editing.name.trim()) { setError('Name is required'); return; }
    if (!editing.contact || !editing.contact.trim()) { setError('Contact number/name is required'); return; }
    setSaving(true); setError('');
    try {
      await api.put(`/leads/${editing.id}`, editing, { headers: authHeaders() });
      setEditing(null);
      await load(); await loadCounts();
    } catch (e) { setError(e.response?.data?.error || 'Could not save lead'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try { await api.delete(`/leads/${id}`, { headers: authHeaders() }); await load(); await loadCounts(); }
    catch { setError('Delete failed'); }
  };

  const pc = PROJECT_COLOR[project];
  const salesmen = currentSalesmen;
  const rowSalesmen = [...new Set([...salesmen, ...rows.map((row) => row.salesman).filter(Boolean)])];
  const adNames = adsOptions.map((a) => a.name);
  const chTag = (c) => (c ? <span className={`tag ${CHANNEL_CLASS[c] || 'sm'}`}>{c}</span> : <span className="empty">—</span>);
  const isJunk = (s) => lc(s) === 'junk';
  const smTag = (s) => (s ? <span className={`tag ${isJunk(s) ? 'junk' : 'sm'}`}>{s}</span> : <span className="empty">—</span>);

  // shared cell editors
  const dateCell = (v, set) => <input className="mk-input" type="date" value={v || ''} onChange={(e) => set(e.target.value)} />;
  const textCell = (v, set) => <input className="mk-input" value={v || ''} onChange={(e) => set(e.target.value)} />;
  const contactCell = (v, set) => <input className="mk-input" value={v || ''} onChange={(e) => set(e.target.value)} />;
  const channelSel = (v, set) => <select className="mk-select" value={v || ''} onChange={(e) => set(e.target.value)}><option value="">—</option>{CHANNELS.map((c) => <option key={c}>{c}</option>)}</select>;
  const salesSel = (v, set) => <select className="mk-select" value={v || ''} onChange={(e) => set(e.target.value)}><option value="">—</option>{rowSalesmen.map((s) => <option key={s}>{s}</option>)}</select>;

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const fmtFull = (iso) => { const [y, m, d] = iso.split('-'); return `${+d} ${MONTHS[+m - 1]} ${y}`; };

  // per-date counts across the full filtered set (shown on each date divider)
  const dateCounts = {};
  filteredRows.forEach((r) => { dateCounts[r.enquiry_date] = (dateCounts[r.enquiry_date] || 0) + 1; });

  // distinct dates present (for the date filter), newest first
  const availableDates = [...new Set(rows.map((r) => r.enquiry_date))].sort().reverse();

  // pagination
  const total = filteredRows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const curPage = Math.min(page, pageCount - 1);
  const start = curPage * pageSize;
  const pageRows = filteredRows.slice(start, start + pageSize);

  // group the page's rows into one <tbody> "day card" per date (accent header + rows)
  const withDateDividers = (colSpan, renderRow) => {
    if (pageRows.length === 0) {
      return <tbody><tr key="empty"><td className="empty" colSpan={colSpan}>{rows.length ? 'No leads match the filters.' : `No ${project} leads for ${month}. Add one above.`}</td></tr></tbody>;
    }
    const groups = [];
    let cur = null;
    pageRows.forEach((r) => {
      if (!cur || cur.date !== r.enquiry_date) { cur = { date: r.enquiry_date, rows: [] }; groups.push(cur); }
      cur.rows.push(r);
    });
    return groups.map((g, gi) => {
      const c = dateCounts[g.date];
      return (
        <tbody className="day-card" key={g.date}>
          {gi > 0 && <tr className="card-gap"><td colSpan={colSpan} /></tr>}
          <tr className="day-head-row">
            <td colSpan={colSpan}><span className="d-label">{fmtFull(g.date)}</span><span className="d-badge">{c} lead{c > 1 ? 's' : ''}</span></td>
          </tr>
          {g.rows.map((r) => renderRow(r))}
        </tbody>
      );
    });
  };

  const renderEventRow = (r) => editing && editing.id === r.id ? (
    <tr key={r.id}>
      <td>{dateCell(editing.enquiry_date, (v) => setEditing({ ...editing, enquiry_date: v }))}</td>
      <td>{textCell(editing.name, (v) => setEditing({ ...editing, name: v }))}</td>
      <td>{contactCell(editing.contact, (v) => setEditing({ ...editing, contact: v }))}</td>
      <td>{channelSel(editing.channel, (v) => setEditing({ ...editing, channel: v }))}</td>
      <td>{salesSel(editing.salesman, (v) => setEditing({ ...editing, salesman: v }))}</td>
      <td className="r"><span className="row-act" onClick={saveEdit}>Save</span> · <span className="row-act" onClick={() => setEditing(null)}>Cancel</span></td>
    </tr>
  ) : (
    <tr key={r.id}>
      <td>{fmtDM(r.enquiry_date)}</td>
      <td>{r.name || <span className="empty">—</span>}</td>
      <td>{r.contact || <span className="empty">—</span>}</td>
      <td>{chTag(r.channel)}</td>
      <td>{smTag(r.salesman)}</td>
      <td className="r"><span className="row-act" onClick={() => startEdit(r)}>Edit</span> · <span className="row-act del" onClick={() => del(r.id)}>Del</span></td>
    </tr>
  );

  const renderRmcRow = (r) => editing && editing.id === r.id ? (
    <tr key={r.id}>
      <td>{dateCell(editing.enquiry_date, (v) => setEditing({ ...editing, enquiry_date: v }))}</td>
      <td>{textCell(editing.name, (v) => setEditing({ ...editing, name: v }))}</td>
      <td>{contactCell(editing.contact, (v) => setEditing({ ...editing, contact: v }))}</td>
      <td>{channelSel(editing.channel, (v) => setEditing({ ...editing, channel: v }))}</td>
      <td>{addingAdEdit ? (
        <div className="ads-add">
          <input className="mk-input" autoFocus placeholder="New ad name" value={newAdEdit}
                 onChange={(e) => setNewAdEdit(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') addAdEdit(); if (e.key === 'Escape') { setAddingAdEdit(false); setNewAdEdit(''); } }} />
          <button type="button" className="mk-btn ads-save" onClick={addAdEdit} title="Save ad">✓</button>
          <button type="button" className="mk-btn" onClick={() => { setAddingAdEdit(false); setNewAdEdit(''); }} title="Cancel">✕</button>
        </div>
      ) : (
        <div className="ads-add">
          <SearchSelect
            value={editing.ads || ''}
            onChange={(v) => setEditing({ ...editing, ads: v })}
            options={editing.ads && !adNames.includes(editing.ads) ? [editing.ads, ...adNames] : adNames}
            placeholder="Type to search…"
          />
          <button type="button" className="mk-btn ads-new" onClick={() => setAddingAdEdit(true)} title="Add new ad">＋</button>
        </div>
      )}</td>
      <td>{salesSel(editing.salesman, (v) => setEditing({ ...editing, salesman: v }))}</td>
      <td>{textCell(editing.remarks, (v) => setEditing({ ...editing, remarks: v }))}</td>
      <td className="r"><span className="row-act" onClick={saveEdit}>Save</span> · <span className="row-act" onClick={() => setEditing(null)}>Cancel</span></td>
    </tr>
  ) : (
    <tr key={r.id} className={isJunk(r.salesman) ? 'junk-row' : ''}>
      <td>{fmtDM(r.enquiry_date)}</td>
      <td>{r.name || <span className="empty">—</span>}</td>
      <td>{r.contact || <span className="empty">—</span>}</td>
      <td>{chTag(r.channel)}</td>
      <td>{r.ads || <span className="empty">—</span>}</td>
      <td>{smTag(r.salesman)}</td>
      <td>{r.remarks || <span className="empty">—</span>}</td>
      <td className="r"><span className="row-act" onClick={() => startEdit(r)}>Edit</span> · <span className="row-act del" onClick={() => del(r.id)}>Del</span></td>
    </tr>
  );

  const pager = (
    <div className="mk-pager">
      <div className="pg-info">Showing <b>{total === 0 ? 0 : start + 1}–{Math.min(start + pageSize, total)}</b> of <b>{total}</b>{activeFilters ? ' filtered' : ''}</div>
      <div className="pg-controls">
        <label className="pg-size">Per page
          <select className="mk-select" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            <option value={20}>20</option><option value={50}>50</option>
          </select>
        </label>
        <button type="button" className="mk-btn" disabled={curPage <= 0} onClick={() => setPage(curPage - 1)}>‹ Prev</button>
        <span className="pg-page">Page {curPage + 1} / {pageCount}</span>
        <button type="button" className="mk-btn" disabled={curPage >= pageCount - 1} onClick={() => setPage(curPage + 1)}>Next ›</button>
      </div>
    </div>
  );

  return (
    <div className="mk-page">
      <div className="mk-head">
        <div>
          <div className="mk-crumbs">Marketing Report &rsaquo; Lead Report &rsaquo; <b>Key in Lead</b> &rsaquo; {project}</div>
          <h1>Key in Lead</h1>
          <div className="sub">{month} · {evt ? 'event-space enquiries' : 'catering enquiries'}, tagged to the active project</div>
        </div>
      </div>

      <div className="mk-banner ok">
        <span>✓</span>
        <span>Pick a <b>project tab</b>, then key its enquiries. {evt ? 'Event spaces track date, name, contact, channel & sales person.' : 'Catering projects also capture the Ads that generated the lead.'} Everything shows on the <b>Dashboard</b> instantly.</span>
      </div>

      {error && <div className="mk-error">{error}</div>}
      {salespeopleError && <div className="mk-error">{salespeopleError}</div>}

      {/* Restricted users see only the project the backend permits them to key. */}
      <div className="proj-tabs">
        {visibleProjects.map((p) => (
          <div key={p} className={`proj-tab ${p === project ? 'active' : ''}`} style={{ '--pc': PROJECT_COLOR[p] }} onClick={() => switchProject(p)}>
            <span className="dot" style={{ background: PROJECT_COLOR[p] }} />{p}
            <span className="cnt">{counts[p] ?? 0}</span>
          </div>
        ))}
      </div>

      {/* Add form */}
      <div className="mk-card add-lead-card">
        <div className="mk-card-head">
          <h3>➕ Add Lead <span className="proj-badge"><span className="dot" style={{ background: pc }} />{project}</span></h3>
          <span className="c-sub">project is set by the active tab</span>
        </div>
        <div className="mk-form leads4">
          <div className="mk-field"><label>Enq Date</label><input className="mk-input" type="date" value={form.enquiry_date} onChange={(e) => setForm({ ...form, enquiry_date: e.target.value })} /></div>
          <div className="mk-field"><label>Name <span className="req">*</span></label><input className="mk-input" placeholder="name or contact no." required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="mk-field"><label>Contact Number/Name <span className="req">*</span></label><input className="mk-input" placeholder="60123456789 or a name" required value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
          <div className="mk-field"><label>Channel</label><select className="mk-select" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>{CHANNELS.map((c) => <option key={c}>{c}</option>)}</select></div>

          {evt ? (
            <div className="mk-field"><label>Sales Person</label><select className="mk-select" value={form.salesman} onChange={(e) => setForm({ ...form, salesman: e.target.value })}>{salesmen.map((s) => <option key={s}>{s}</option>)}</select></div>
          ) : (
            <>
              <div className="mk-field">
                <label>Ads</label>
                {addingAd ? (
                  <div className="ads-add">
                    <input className="mk-input" autoFocus placeholder="New ad name" value={newAd}
                           onChange={(e) => setNewAd(e.target.value)}
                           onKeyDown={(e) => { if (e.key === 'Enter') addAd(); if (e.key === 'Escape') { setAddingAd(false); setNewAd(''); } }} />
                    <button type="button" className="mk-btn ads-save" onClick={addAd} title="Save ad">✓</button>
                    <button type="button" className="mk-btn" onClick={() => { setAddingAd(false); setNewAd(''); }} title="Cancel">✕</button>
                  </div>
                ) : (
                  <div className="ads-add">
                    <SearchSelect
                      value={form.ads}
                      onChange={(v) => setForm({ ...form, ads: v })}
                      options={adNames}
                      placeholder="Type to search…"
                    />
                    <button type="button" className="mk-btn ads-new" onClick={() => setAddingAd(true)} title="Add new ad">＋ New</button>
                  </div>
                )}
              </div>
              <div className="mk-field"><label>Sales Person</label><select className="mk-select" value={form.salesman} onChange={(e) => setForm({ ...form, salesman: e.target.value })}>{salesmen.map((s) => <option key={s}>{s}</option>)}</select></div>
              <div className="mk-field"><label>Remark</label><input className="mk-input" placeholder="optional" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
            </>
          )}

          <button className="mk-btn primary" disabled={saving} onClick={add}>{saving ? '…' : '+ Add'}</button>
        </div>
        <p className="mk-hint"><b>Name</b> and <b>Contact Number/Name</b> are required. Project is the active <b>{project}</b> tab; <b>Month/Day</b> come from the Enq Date automatically.</p>
      </div>

      {/* Table */}
      <div className="mk-card">
        <div className="mk-card-head"><h3>{project} Leads — {month}</h3><span className="c-sub">{activeFilters ? `${filteredRows.length} of ${rows.length}` : `${rows.length}`} enquiries</span></div>

        <div className="mk-filters">
          <input className="mk-input f-search" placeholder="🔍 Search name or contact…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
          <select className="mk-select" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value })}>
            <option value="">All dates</option>{availableDates.map((d) => <option key={d} value={d}>{fmtFull(d)}</option>)}
          </select>
          <select className="mk-select" value={filters.channel} onChange={(e) => setFilters({ ...filters, channel: e.target.value })}>
            <option value="">All channels</option>{CHANNELS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select className="mk-select" value={filters.salesman} onChange={(e) => setFilters({ ...filters, salesman: e.target.value })}>
            <option value="">All sales</option>{rowSalesmen.map((s) => <option key={s}>{s}</option>)}
          </select>
          {!evt && (
            <div className="f-ads">
              <SearchSelect
                value={filters.ads}
                onChange={(v) => setFilters({ ...filters, ads: v })}
                options={adNames}
                placeholder="Type to search…"
                emptyLabel="All ads"
              />
            </div>
          )}
          {activeFilters && <button type="button" className="mk-btn" onClick={clearFilters}>Clear</button>}
        </div>

        <div className="mk-tbl-scroll">
          {evt ? (
            <table className="mk-tbl left leads-grouped">
              <thead><tr><th>Enq Date</th><th>Name</th><th>Contact No./Name</th><th>Channel</th><th>Sales Person</th><th className="r">Actions</th></tr></thead>
              {withDateDividers(6, renderEventRow)}
            </table>
          ) : (
            <table className="mk-tbl left leads-grouped">
              <thead><tr><th>Enq Date</th><th>Name</th><th>Contact No./Name</th><th>Channel</th><th>Ads</th><th>Sales Person</th><th>Remark</th><th className="r">Actions</th></tr></thead>
              {withDateDividers(8, renderRmcRow)}
            </table>
          )}
        </div>
        {pager}
        <p className="mk-foot">Rows are grouped by enquiry date with a per-day count. Switch project tabs above to key a different project.</p>
      </div>
    </div>
  );
}

export default MarketingLeads;
